import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Line, Bar } from "react-chartjs-2";
import {
  Card,
  CardHeader,
  CardBody,
  Row,
  Col,
  FormGroup,
  Label,
  Input,
  Button,
  Table,
} from "reactstrap";
import axios from "../../config/axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

function num(v) {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
}

const MetaAdsAnalytics = () => {
  const { token: authToken } = useSelector((state) => state.auth);
  const [adAccounts, setAdAccounts] = useState([]);
  const [selectedAccountIdx, setSelectedAccountIdx] = useState(0);
  const [datePreset, setDatePreset] = useState("last_7_days");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const headers = () => ({ Authorization: `Bearer ${authToken}` });
  const selectedAccount = adAccounts[selectedAccountIdx];

  useEffect(() => {
    if (!authToken) return;
    axios
      .get("/ad-accounts", { headers: headers() })
      .then((res) => setAdAccounts(res.data.data || []))
      .catch(() => setAdAccounts([]));
  }, [authToken]);

  const loadInsights = () => {
    if (!selectedAccount?.accountId) return;
    setLoading(true);
    axios
      .get(`/meta/insights/account/${encodeURIComponent(selectedAccount.accountId)}?date_preset=${encodeURIComponent(datePreset)}`, {
        headers: headers(),
      })
      .then((res) => setRows(res.data.data || []))
      .catch((e) => toast.error(e.response?.data?.message || "Failed to load insights"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!selectedAccount?.accountId) return;
    loadInsights();
  }, [selectedAccount?.accountId, datePreset]);

  const totals = useMemo(() => {
    const t = {
      impressions: 0,
      reach: 0,
      clicks: 0,
      spend: 0,
      results: 0,
    };
    for (const r of rows) {
      t.impressions += num(r.impressions);
      t.reach += num(r.reach);
      t.clicks += num(r.clicks);
      t.spend += num(r.spend);
      const actions = Array.isArray(r.actions) ? r.actions : [];
      const actionPriority = ["purchase", "offsite_conversion.purchase", "lead", "add_to_cart", "link_click"];
      let rowResult = 0;
      for (const type of actionPriority) {
        const hit = actions.find((a) => a.action_type === type);
        if (hit) {
          rowResult = num(hit.value);
          break;
        }
      }
      if (!rowResult && actions.length > 0) {
        rowResult = num(actions[0].value);
      }
      t.results += rowResult;
    }
    const ctr = t.impressions ? (t.clicks / t.impressions) * 100 : 0;
    const cpc = t.clicks ? t.spend / t.clicks : 0;
    return { ...t, ctr, cpc };
  }, [rows]);

  const chartData = useMemo(() => {
    const labels = rows.map((r) => r.date_start || r.date_stop || "");
    const spend = rows.map((r) => num(r.spend));
    const clicks = rows.map((r) => num(r.clicks));
    const impressions = rows.map((r) => num(r.impressions));
    return { labels, spend, clicks, impressions };
  }, [rows]);

  const spendLine = {
    labels: chartData.labels,
    datasets: [
      {
        label: "Spend (USD)",
        data: chartData.spend,
        borderColor: "#20a8d8",
        backgroundColor: "rgba(32,168,216,0.1)",
        fill: true,
      },
    ],
  };

  const clicksImpressionsBar = {
    labels: chartData.labels,
    datasets: [
      {
        label: "Clicks",
        data: chartData.clicks,
        backgroundColor: "rgba(77,189,116,0.6)",
      },
      {
        label: "Impressions",
        data: chartData.impressions,
        backgroundColor: "rgba(32,168,216,0.4)",
      },
    ],
  };

  return (
    <div className="animated fadeIn">
      <ToastContainer autoClose={5000} />
      <Row>
        <Col>
          <Card>
            <CardHeader className="d-flex justify-content-between align-items-center">
              <b>Meta Ads Analytics</b>
              <Button color="secondary" size="sm" onClick={loadInsights} disabled={loading || !selectedAccount?.accountId}>
                {loading ? "Refreshing..." : "Refresh"}
              </Button>
            </CardHeader>
            <CardBody>
              <Row form>
                <Col md={6}>
                  <FormGroup>
                    <Label>Ad account</Label>
                    <Input
                      type="select"
                      value={String(selectedAccountIdx)}
                      onChange={(e) => setSelectedAccountIdx(Number(e.target.value))}
                    >
                      {adAccounts.map((a, idx) => (
                        <option key={a.id} value={idx}>
                          {a.accountName || a.accountId}
                        </option>
                      ))}
                    </Input>
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <FormGroup>
                    <Label>Period</Label>
                    <Input type="select" value={datePreset} onChange={(e) => setDatePreset(e.target.value)}>
                      <option value="last_7_days">Last 7 days</option>
                      <option value="last_14_days">Last 14 days</option>
                      <option value="last_30_days">Last 30 days</option>
                    </Input>
                  </FormGroup>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={2}>
                  <Card body>
                    <b>Impressions</b>
                    <div>{totals.impressions.toLocaleString()}</div>
                  </Card>
                </Col>
                <Col md={2}>
                  <Card body>
                    <b>Clicks</b>
                    <div>{totals.clicks.toLocaleString()}</div>
                  </Card>
                </Col>
                <Col md={2}>
                  <Card body>
                    <b>CTR</b>
                    <div>{totals.ctr.toFixed(2)}%</div>
                  </Card>
                </Col>
                <Col md={2}>
                  <Card body>
                    <b>Spend</b>
                    <div>${totals.spend.toFixed(2)}</div>
                  </Card>
                </Col>
                <Col md={2}>
                  <Card body>
                    <b>Results</b>
                    <div>{totals.results.toLocaleString()}</div>
                  </Card>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Card>
                    <CardHeader><b>Spend over time</b></CardHeader>
                    <CardBody>
                      <Line data={spendLine} />
                    </CardBody>
                  </Card>
                </Col>
                <Col md={6}>
                  <Card>
                    <CardHeader><b>Clicks vs Impressions</b></CardHeader>
                    <CardBody>
                      <Bar data={clicksImpressionsBar} />
                    </CardBody>
                  </Card>
                </Col>
              </Row>

              <Card className="mt-3">
                <CardHeader><b>Daily performance</b></CardHeader>
                <CardBody>
                  <Table bordered striped responsive size="sm">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Impressions</th>
                        <th>Reach</th>
                        <th>Clicks</th>
                        <th>Spend</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan={5} className="py-4 text-center">
                            <i className="fas fa-spinner fa-pulse fa-2x" aria-hidden />
                          </td>
                        </tr>
                      ) : rows.length === 0 ? (
                        <tr>
                          <td colSpan={5} className="py-4 text-center text-muted">
                            No data.
                          </td>
                        </tr>
                      ) : (
                        rows.map((r, idx) => (
                          <tr key={idx}>
                            <td>{r.date_start || "-"}</td>
                            <td>{num(r.impressions).toLocaleString()}</td>
                            <td>{num(r.reach).toLocaleString()}</td>
                            <td>{num(r.clicks).toLocaleString()}</td>
                            <td>${num(r.spend).toFixed(2)}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </Table>
                </CardBody>
              </Card>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default MetaAdsAnalytics;
